import { useState } from 'react'
import { Check, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { useAdminMedia } from '../../hooks/useAdminMedia'
import { useAdminMediaFolders } from '../../hooks/useAdminMediaFolders'
import { MediaFolderTree } from './MediaFolderTree'
import type { MediaDocument } from '../../services/admin.media.service'

interface MediaLibraryPickerDialogProps {
  open: boolean
  onClose: () => void
  onSelect: (url: string) => void
  onSelectItem?: (item: MediaDocument) => void
}

export function MediaLibraryPickerDialog({ open, onClose, onSelect, onSelectItem }: MediaLibraryPickerDialogProps) {
  const [folderId, setFolderId] = useState<string | null>(null)
  const [selectedItem, setSelectedItem] = useState<MediaDocument | null>(null)
  const [search, setSearch] = useState('')

  const { folders, createFolder, renameFolder, deleteFolder } = useAdminMediaFolders()
  const { items, loading } = useAdminMedia({ folderId })

  const readyImages = items
    .filter((item) => item.status === 'ready' && item.mimeType.startsWith('image/'))
    .filter((item) => !search.trim() || item.name.toLowerCase().includes(search.toLowerCase()))

  function handleConfirm() {
    if (!selectedItem) return
    onSelectItem?.(selectedItem)
    onSelect(selectedItem.url)
    onClose()
    setSelectedItem(null)
  }

  function handleClose() {
    onClose()
    setSelectedItem(null)
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose() }}>
      <DialogContent className="max-w-4xl h-[600px] flex flex-col gap-0 p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <div className="flex items-center gap-4">
            <DialogTitle className="shrink-0">Select from Media Library</DialogTitle>
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="pl-8 h-8 text-sm"
              />
            </div>
          </div>
        </DialogHeader>

        <div className="flex flex-1 min-h-0">
          {/* Folder sidebar */}
          <div className="w-48 shrink-0 border-r overflow-y-auto">
            <MediaFolderTree
              folders={folders}
              selectedFolderId={folderId}
              onSelect={(id) => { setFolderId(id); setSelectedItem(null); setSearch('') }}
              onCreate={async (name, parentId) => { await createFolder(name, parentId) }}
              onRename={renameFolder}
              onDelete={deleteFolder}
            />
          </div>

          {/* Image grid */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="aspect-square animate-pulse rounded-lg bg-muted" />
                ))}
              </div>
            ) : readyImages.length === 0 ? (
              <div className="flex h-40 items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
                No images in this folder.
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                {readyImages.map((item) => {
                  const isSelected = selectedItem?.id === item.id
                  return (
                    <button
                      key={item.id}
                      onClick={() => setSelectedItem(isSelected ? null : item)}
                      className={cn(
                        'group relative aspect-square overflow-hidden rounded-lg border-2 transition-all',
                        isSelected
                          ? 'border-primary ring-2 ring-primary ring-offset-2'
                          : 'border-transparent hover:border-muted-foreground/40'
                      )}
                    >
                      <img
                        src={item.thumbnailUrl ?? item.url}
                        loading="lazy"
                        alt={item.name}
                        className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                      />
                      {isSelected && (
                        <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                          <div className="rounded-full bg-primary p-1">
                            <Check className="h-4 w-4 text-primary-foreground" />
                          </div>
                        </div>
                      )}
                      <p className="absolute bottom-0 left-0 right-0 truncate bg-black/50 px-1.5 py-1 text-[10px] text-white opacity-0 transition-opacity group-hover:opacity-100">
                        {item.name}
                      </p>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={!selectedItem}>
            Use Selected Image
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
